import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { ListTodo, CalendarCheck, ArrowRightLeft, TriangleAlert, Utensils, UserPlus, Bell } from "lucide-react";
import Shell from "@/components/Shell";
import { Panel, Btn, Field, TextArea, Select, Input, StatusBadge, AllergyFlag } from "@/components/kit";
import { NotificationsPanel } from "@/components/NotificationsPanel";
import { useAuth, portalForUser } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

// Department-specific wording. Each department gets its own portal with tailored labels.
const DEPT_META = {
  Kitchen: { title: "Kitchen", tasks: "Kitchen Tasks", bookings: "Kitchen Bookings" },
  "Food & Beverage": { title: "Food & Beverage", tasks: "Service Tasks", bookings: "Dining Bookings" },
  Housekeeping: { title: "Housekeeping", tasks: "Room & Service Tasks", bookings: "Service Bookings" },
  Maintenance: { title: "Maintenance", tasks: "Work Orders", bookings: "Scheduled Jobs" },
  Events: { title: "Events", tasks: "Event Tasks", bookings: "Event Bookings" },
};

export default function StaffPortal({ expectDept }) {
  const { user } = useAuth();
  const isManager = user?.role === "manager";
  const dept = user?.department_name;
  const [active, setActive] = useState("dashboard");
  const [complaints, setComplaints] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [handovers, setHandovers] = useState([]);
  const [escalations, setEscalations] = useState([]);
  const isAdmin = user?.role === "admin";

  const load = () => {
    api.get("/complaints").then((r) => setComplaints(r.data)).catch(() => {});
    api.get("/bookings").then((r) => setBookings(r.data)).catch(() => {});
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/handovers").then((r) => setHandovers(r.data)).catch(() => {});
    api.get("/escalations").then((r) => setEscalations(r.data)).catch(() => {});
    if (isManager) api.get("/staff").then((r) => setStaff(r.data)).catch(() => {});
  };
  useEffect(load, []);

  // Keep each department on its own portal URL.
  if (expectDept && !isAdmin && dept !== expectDept) return <Navigate to={portalForUser(user)} replace />;

  const viewDept = isAdmin && expectDept ? expectDept : dept;
  const previewDeptName = isAdmin && expectDept ? expectDept : undefined;
  const meta = DEPT_META[viewDept];
  const nav = [
    { key: "dashboard", label: "Dashboard", icon: Bell },
    { key: "tasks", label: meta?.tasks || "My Tasks", icon: ListTodo },
    { key: "bookings", label: meta?.bookings || "Bookings", icon: CalendarCheck },
    { key: "services", label: "My Services", icon: Utensils },
    { key: "handover", label: "Handover", icon: ArrowRightLeft },
    { key: "escalation", label: "Escalations", icon: TriangleAlert },
  ];
  const title = meta ? meta.title : (viewDept ? `My Department · ${viewDept}` : "Staff Portal");
  const subtitle = isAdmin && expectDept ? "Management preview" : `${user?.title || ""}${isManager ? " · Department Manager" : ""}`;
  const isFood = ["Kitchen", "Food & Beverage"].includes(viewDept);
  // When management previews a department portal, scope the data to that department.
  const scope = (arr) => (isAdmin && expectDept ? arr.filter((x) => x.department_name === viewDept) : arr);

  return (
    <Shell title={title} subtitle={subtitle} nav={nav} active={active} onNav={setActive}>
      {active === "dashboard" && <NotificationsPanel title="Department updates" intro="Assignments, handovers and escalations for your department appear here." deptName={previewDeptName} />}
      {active === "tasks" && <Tasks complaints={scope(complaints)} reload={load} isManager={isManager} staff={staff} deptId={user?.department_id} />}
      {active === "bookings" && <Bookings bookings={scope(bookings)} reload={load} isFood={isFood} />}
      {active === "services" && <Services services={scope(services)} />}
      {active === "handover" && <Handover items={scope(handovers)} reload={load} user={user} />}
      {active === "escalation" && <Escalations items={scope(escalations)} reload={load} user={user} />}
    </Shell>
  );
}

function Services({ services }) {
  if (!services.length) return <Empty text="No services connected to your department yet." />;
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {services.map((s) => (
        <Panel key={s.id} className="p-5" data-testid={`sservice-${s.id}`}>
          <div className="flex justify-between items-start"><h3 className="font-head font-bold text-white">{s.name}</h3>{s.food_related && <span className="text-xs text-rose-300 font-semibold">food</span>}</div>
          <p className="text-xs text-purple-400 mt-1">Capacity {s.capacity} · {s.duration_min} min · {s.available ? "available" : "unavailable"}</p>
          {s.description && <p className="text-sm text-purple-200/70 mt-2">{s.description}</p>}
        </Panel>
      ))}
    </div>
  );
}

function Tasks({ complaints, reload, isManager, staff, deptId }) {
  const [notes, setNotes] = useState({});
  const [assignSel, setAssignSel] = useState({});
  const update = async (c, status) => {
    try {
      await api.patch(`/complaints/${c.id}/status`, { status, resolution_note: notes[c.id] || c.resolution_note });
      toast.success(`Marked ${status.replace("_", " ")}`); reload();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const assign = async (c) => {
    const staffId = assignSel[c.id];
    if (!staffId) return toast.error("Pick a team member");
    try {
      await api.patch(`/complaints/${c.id}/assign`, { department_id: deptId, staff_id: staffId });
      toast.success("Assigned to team member"); reload();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  if (!complaints.length) return <Empty text="No complaints assigned to your department." />;
  return (
    <div className="space-y-4">
      {complaints.map((c) => (
        <Panel key={c.id} className="p-5" data-testid={`task-${c.id}`}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2"><h3 className="font-head font-bold text-white">{c.subject}</h3><code className="text-[11px] text-amber-300">{c.reference}</code></div>
              <p className="text-xs text-purple-400 mt-0.5">{c.category} · {c.guest_name}{c.assigned_to_name ? ` · assigned to ${c.assigned_to_name}` : ""}</p>
            </div>
            <StatusBadge status={c.status} />
          </div>
          <p className="text-sm text-purple-200/80 mt-2">{c.description}</p>
          {isManager && c.status !== "resolved" && (
            <div className="mt-3 flex flex-wrap items-end gap-2 p-3 rounded-xl bg-[#230A4A]/60 border border-[#2E1256]">
              <span className="text-xs font-semibold uppercase tracking-wider text-purple-300/60 w-full flex items-center gap-1"><UserPlus size={13} /> Assign within department</span>
              <div className="min-w-[180px]"><Select data-testid={`mgr-assign-sel-${c.id}`} value={assignSel[c.id] || ""} onChange={(e) => setAssignSel({ ...assignSel, [c.id]: e.target.value })}>
                <option value="">Team member…</option>
                {staff.filter((s) => s.role === "staff").map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </Select></div>
              <Btn variant="secondary" data-testid={`mgr-assign-${c.id}`} onClick={() => assign(c)}>Assign</Btn>
            </div>
          )}
          {c.status !== "resolved" ? (
            <div className="mt-4 space-y-3">
              <TextArea data-testid={`task-note-${c.id}`} placeholder="Resolution notes (required to resolve)…"
                defaultValue={c.resolution_note || ""} onChange={(e) => setNotes({ ...notes, [c.id]: e.target.value })} />
              <div className="flex flex-wrap gap-2">
                {c.status !== "in_progress" && <Btn variant="secondary" data-testid={`task-progress-${c.id}`} onClick={() => update(c, "in_progress")}>Accept &amp; start</Btn>}
                <Btn data-testid={`task-resolve-${c.id}`} onClick={() => { if (!(notes[c.id] || c.resolution_note)) return toast.error("Add resolution notes first"); update(c, "resolved"); }}>Resolve</Btn>
              </div>
            </div>
          ) : c.resolution_note && <p className="mt-3 text-sm text-emerald-300">Resolved: {c.resolution_note}</p>}
        </Panel>
      ))}
    </div>
  );
}

function Bookings({ bookings, reload, isFood }) {
  const ack = async (b) => {
    try { await api.patch(`/bookings/${b.id}/acknowledge-allergy`); toast.success("Allergy acknowledged"); reload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const complete = async (b) => {
    try { await api.patch(`/bookings/${b.id}/status`, { status: "completed" }); toast.success("Booking completed"); reload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  if (!bookings.length) return <Empty text="No bookings for your department." />;
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {bookings.map((b) => (
        <Panel key={b.id} className="p-5" data-testid={`sbooking-${b.id}`}>
          <div className="flex items-start justify-between gap-3 mb-2">
            <div><h3 className="font-head font-bold text-white">{b.service_name}</h3><p className="text-xs text-purple-400">{b.guest_name} · {b.date} {b.time} · party {b.party_size}</p></div>
            <StatusBadge status={b.status} />
          </div>
          {b.allergy_info && <div className="my-2"><AllergyFlag info={b.allergy_info} />{b.allergy_ack && <span className="ml-2 text-xs text-emerald-300">acknowledged</span>}</div>}
          {b.notes && <p className="text-xs text-purple-400/70">"{b.notes}"</p>}
          <div className="flex gap-2 mt-3">
            {isFood && b.allergy_info && !b.allergy_ack && <Btn variant="secondary" data-testid={`ack-${b.id}`} onClick={() => ack(b)}>Acknowledge allergy</Btn>}
            {b.status === "approved" && <Btn data-testid={`complete-${b.id}`} onClick={() => complete(b)}>Mark completed</Btn>}
          </div>
        </Panel>
      ))}
    </div>
  );
}

function Handover({ items, reload, user }) {
  const [note, setNote] = useState("");
  const add = async () => {
    if (!note.trim()) return;
    try { await api.post("/handovers", { location_id: user.location_id, note }); setNote(""); toast.success("Handover added"); reload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <div className="space-y-4 max-w-2xl">
      <Panel className="p-5">
        <Field label="New handover note"><TextArea data-testid="handover-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Pass operational info to the next shift…" /></Field>
        <Btn className="mt-3" data-testid="handover-add" onClick={add}>Post handover</Btn>
      </Panel>
      {items.map((h) => (
        <Panel key={h.id} className="p-4" data-testid={`handover-${h.id}`}>
          <p className="text-sm text-purple-100">{h.note}</p>
          <p className="text-xs text-purple-400 mt-2">{h.author} · {h.department_name} · {new Date(h.created_at).toLocaleString()}</p>
        </Panel>
      ))}
    </div>
  );
}

function Escalations({ items, reload, user }) {
  const [form, setForm] = useState({ title: "", detail: "", severity: "high" });
  const add = async () => {
    if (!form.title.trim()) return;
    try { await api.post("/escalations", { location_id: user.location_id, ...form }); setForm({ title: "", detail: "", severity: "high" }); toast.success("Escalated"); reload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <div className="space-y-4 max-w-2xl">
      <Panel className="p-5 space-y-3">
        <Field label="Escalation title"><Input data-testid="esc-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Critical issue" /></Field>
        <Field label="Detail"><TextArea data-testid="esc-detail" value={form.detail} onChange={(e) => setForm({ ...form, detail: e.target.value })} /></Field>
        <Field label="Severity"><Select data-testid="esc-severity" value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })}>{["low", "high", "critical"].map((s) => <option key={s}>{s}</option>)}</Select></Field>
        <Btn data-testid="esc-add" onClick={add}>Raise escalation</Btn>
      </Panel>
      {items.map((e) => (
        <Panel key={e.id} className="p-4" data-testid={`esc-${e.id}`}>
          <div className="flex items-center justify-between"><h3 className="font-bold text-white">{e.title}</h3><StatusBadge status={e.status} /></div>
          <p className="text-sm text-purple-200/80 mt-1">{e.detail}</p>
          <p className="text-xs text-rose-300 mt-2 uppercase font-semibold">{e.severity} · {e.author}</p>
        </Panel>
      ))}
    </div>
  );
}

function Empty({ text }) { return <Panel className="p-12 text-center text-purple-400/70">{text}</Panel>; }
